import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/dom/ajax';
import buildApiUrl from '../urls/buildApiUrl';
import { getCookie } from '../cookie';
import pick from 'lodash/pick';

import type { AjaxOptions, EndpointOptions } from '../../types';

/**
 * A utility for creating Ajax observables that fetch and return data from the
 * Paperless Post API. Could be converted for a more general ajax utility, but
 * offers a consistent way to quickly create new api utils.
 *
 * The createAjax function is a manually curried utility so that we can partially
 * apply arguments to build more utilities (see some of the utility methods below)
 * to pass around the application. You will probably never need to use createAjax
 * directly.
 *
 * createAjaxUtil -> createEndpointUtil -> endpointUtil
 *
 * createAjaxUtil
 * @param {Object} options - The ajax options object
 * @param {'GET' | 'POST' | 'PUT' | 'DELETE'} [options.method='GET'] - the http
 *     method to be used with this util.
 * @param {string} [options.responseType='json'] - Sets the response type of the
 *     request (for apis you probably want the default, 'json')
 * @param {boolean} [options.withCredentials=true] - whether or not this call
 *     should send auth cookies along with the request
 * @return {function} a function that will create a util of the given method
 *
 * @typedef {function} httpUtil
 * @param {Object} options - The endpoint options object
 * @param {string[]?} options.allowedParams- the params (query string or post body)
 *     that the request should allow.
 * @param {string|function} options.path - Either a simple string mapping to an
 *     endpoint or a function that is passed the request params and must return
 *     a string. Allows you to inject params into the path.
 * @param {boolean?} options.csrf - Set to true to include the CSRF token stored
 *     in the cookie as a X-CSRF-Token header.
 * @param {string?} options.contentType - allows you to specify the contentType
 *     of the post/put payload. The ajax call will default to a form content-type,
 *     so if you want to use json, specify that here.
 * @param {Object?} options.forcedParams - If you'd like to force a param to always
 *     be included with ajax calls, specify it here.
 * @return {function} the api util function that will fetch or send the data you request.
 *
 * @typedef {function} endpointUtil
 * @param {Object} params - the parameters of your request.
 * @param {Object} headers - any headers to be sent on your request.
 * @return {AjaxObservable} an Observable of the response.
 */

export function createAjax({
  method = 'GET',
  responseType = 'json',
  withCredentials = true,
}: AjaxOptions = {}) {
  return function httpUtil({
    allowedParams = [],
    path,
    json = false,
    csrf = false,
    forcedParams = {},
  }: EndpointOptions = {}) {
    return function endpointUtil(params: Object = {}, headers: Object = {}) {
      const urlPath = typeof path === 'function' ? path(params) : path;
      if (json) {
        headers['Content-Type'] = 'application/json';
      }

      if (csrf) {
        headers['X-CSRF-Token'] = window.decodeURIComponent(
          getCookie('_csrf_token'),
        );
      }

      headers['X-Application'] = 'paper-browser-redux';

      let body = json ? params : {};

      if (json) {
        if (allowedParams.length) {
          body = pick(body, allowedParams);
        }

        body = Object.assign(body, forcedParams);
      }

      const url = json
        ? buildApiUrl(urlPath)
        : buildApiUrl(urlPath, allowedParams, forcedParams, params);

      // TODO: Could potentially add error handling here and sanitize all error messages

      return Observable.ajax({
        body,
        headers,
        responseType,
        method,
        url,
        withCredentials,
      }).map(data => data.response, null);
    };
  };
}

/**
 * A utility for constructing GET requests
 * @type {httpUtil}
 */
export const createGet = createAjax({
  method: 'GET',
});

/**
 * A utility for constructing DELETE requests
 * @type {httpUtil}
 */
export const createDelete = createAjax({
  method: 'DELETE',
});

/**
 * A utility for constructing POST requests
 * @type {httpUtil}
 */
export const createPost = createAjax({
  method: 'POST',
});

/**
 * A utility for constructing POST requests
 * @type {httpUtil}
 */
export const createPut = createAjax({
  method: 'PUT',
});
